import type { User } from 'oidc-client-ts';
import { userManager } from './userManager';

/**
 * Framework-agnostic session-expiry controller.
 *
 * A single source of truth for "the user's session is dead and they must
 * re-authenticate". Every trigger path — proactive idle expiry, a tab-focus
 * re-check, a failed silent renew, or an unrecoverable 401 from any axios
 * layer / TanStack Query — funnels through here so the SAME global modal is
 * shown exactly once, and so on-demand silent renews are de-duplicated.
 *
 * It deliberately holds no React state; the modal subscribes to it.
 */

type SessionExpiryListener = (expired: boolean) => void;

/** Treat a token as needing renewal this many seconds before its hard expiry. */
const TOKEN_EXPIRY_SKEW_SECONDS = 30;

let sessionExpired = false;
/** Set during an intentional logout so a trailing 401 can't pop the modal. */
let suppressed = false;
const listeners = new Set<SessionExpiryListener>();

const emit = (): void => {
  for (const listener of listeners) listener(sessionExpired);
};

const isImpersonating = (): boolean =>
  sessionStorage.getItem('motr_impersonating') === 'true';

/** Subscribe to session-expiry state changes. Returns an unsubscribe fn. */
export const subscribeSessionExpiry = (
  listener: SessionExpiryListener
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const isSessionExpired = (): boolean => sessionExpired;

/**
 * Show the session-expired modal. The boolean guard collapses a burst of
 * parallel 401s into a single modal instead of N.
 */
export const triggerSessionExpired = (): void => {
  if (suppressed || sessionExpired) return;
  sessionExpired = true;
  emit();
};

/** Clear the expired state (e.g. after a successful silent renew / fresh login). */
export const resetSessionExpiry = (): void => {
  if (!sessionExpired) return;
  sessionExpired = false;
  emit();
};

/**
 * Suppress the modal for the rest of this page's lifetime. Called by an
 * explicit logout, which navigates away anyway — we just don't want a
 * trailing 401 from an in-flight request to flash the modal first.
 */
export const suppressSessionExpiry = (): void => {
  suppressed = true;
};

/**
 * Undo {@link suppressSessionExpiry}. Used when an intentional logout fails to
 * navigate away — otherwise the modal would stay suppressed for the rest of the
 * page's lifetime and a genuine later expiry would be silently swallowed.
 */
export const unsuppressSessionExpiry = (): void => {
  suppressed = false;
};

// ── On-demand silent renew (de-duplicated) ────────────────────────────────
let renewInFlight: Promise<User | null> | null = null;

const attemptSilentRenew = (): Promise<User | null> => {
  if (!renewInFlight) {
    renewInFlight = userManager
      .signinSilent()
      .catch(() => null)
      .finally(() => {
        renewInFlight = null;
      });
  }
  return renewInFlight;
};

/**
 * Renew-or-fail access-token getter used by the axios interceptors.
 *
 * - Returns the current token when it is still comfortably valid.
 * - When missing / near expiry, attempts a de-duplicated on-demand silent
 *   renew. On success returns the fresh token; on failure it triggers the
 *   session-expired flow and returns `null` so the caller fails cleanly
 *   instead of sending a stale token.
 * - Returns `null` (without a modal) when there is no user at all — that's a
 *   normal unauthenticated state which AuthGuard handles via redirect.
 * - During impersonation, silent renew is intentionally disabled, so the
 *   impersonated token is returned as-is (its expiry is handled separately).
 */
export const getValidAccessToken = async (): Promise<string | null> => {
  try {
    const user = await userManager.getUser();
    if (!user) return null;

    if (isImpersonating()) {
      return user.access_token ?? null;
    }

    const now = Math.floor(Date.now() / 1000);
    const nearExpiry =
      !user.access_token ||
      (user.expires_at !== undefined &&
        user.expires_at - now <= TOKEN_EXPIRY_SKEW_SECONDS);

    if (!nearExpiry) {
      return user.access_token;
    }

    const renewed = await attemptSilentRenew();
    if (renewed?.access_token && !renewed.expired) {
      resetSessionExpiry();
      return renewed.access_token;
    }

    triggerSessionExpired();
    return null;
  } catch {
    triggerSessionExpired();
    return null;
  }
};

/**
 * Centralized handler for a 401 that an axios layer / TanStack Query could not
 * silently recover. Tries one more silent renew (the token may have been
 * revoked despite the clock saying it's valid); if that fails, shows the modal.
 * No-ops when there is no user (genuinely unauthenticated) or while
 * impersonating (which has its own expiry handling).
 */
export const handleUnauthorizedResponse = async (): Promise<void> => {
  try {
    const user = await userManager.getUser();
    if (!user) return;
    if (isImpersonating()) return;

    const renewed = await attemptSilentRenew();
    if (renewed?.access_token && !renewed.expired) {
      resetSessionExpiry();
      return;
    }
    triggerSessionExpired();
  } catch {
    triggerSessionExpired();
  }
};

/**
 * Re-validate the session after the tab regains focus. If the stored user has
 * hard-expired and a silent renew can't recover it, show the modal.
 */
export const revalidateSession = async (): Promise<void> => {
  if (isImpersonating()) return;
  try {
    const user = await userManager.getUser();
    if (!user || !user.expired) return;

    const renewed = await attemptSilentRenew();
    if (renewed?.access_token && !renewed.expired) {
      resetSessionExpiry();
    } else {
      triggerSessionExpired();
    }
  } catch {
    triggerSessionExpired();
  }
};
